import Styles from './AiGeneration.module.scss'
import { useEffect, useState } from 'react'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useGenerateImage } from 'hooks/queries/workspaces/useGenerateImage'
import { ImageGenerateRequestGeneratedContent, ImagePrompt, SuggestionRequestStatus } from 'types'
import { Preview } from './Preview'
import { AiGenerationProps } from './Types'
import { Portal } from 'components/Portal/Portal'
import { useImagePickerContext } from '../ImagePickerContext'
import { useMaybeAISuggestionRequest } from './useMaybeAiSuggestionRequest'
import { Form } from './Form'
import { ConfirmSelectionButton } from '../ConfirmSelectionButton'
import { useConfirm } from './useConfirm'

const AiGeneration = ({ actionsContainerRef }: AiGenerationProps) => {
  const workspaceId = useCurrentWorkspaceId()
  const [requestId, setRequestId] = useState<number>()
  const { selectedImages, setSelectedImages, setIsAIGenerating } = useImagePickerContext()
  const aiQuery = useMaybeAISuggestionRequest({ requestId })
  const generateImageMutation = useGenerateImage({ workspaceId })
  const { confirm, isConfirming } = useConfirm()

  const handleGenerate = async (
    values: Omit<ImagePrompt, 'generation_type' | 'image_style' | 'image_mood'> & { model?: 'mystic' | 'openai' },
  ) => {
    setSelectedImages([])
    try {
      const response = await generateImageMutation.mutateAsync({
        model: values.model,
        prompt: {
          topic: values.topic,
          image_type: values.image_type,
          image_aspect_ratio: values.image_aspect_ratio,
          priority: values.priority,
        },
      })
      setRequestId(response.request.id)
    } catch (error) {
      reportError(error)
    }
  }

  useEffect(
    function setGeneratedImageAsSelected() {
      if (!aiQuery.data?.request.generated_content) return

      const generatedImages: ImageGenerateRequestGeneratedContent = JSON.parse(aiQuery.data.request.generated_content)

      setSelectedImages(generatedImages.slice(0, 1))
    },
    [aiQuery.data], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const isGenerating =
    aiQuery.isLoading ||
    aiQuery.data?.request.status === SuggestionRequestStatus.Processing ||
    generateImageMutation.isLoading

  useEffect(() => {
    setIsAIGenerating(isGenerating)
  }, [isGenerating, setIsAIGenerating])

  return (
    <div className={Styles.aiGeneration} data-testid="ai-provider">
      {actionsContainerRef && (
        <Portal container={actionsContainerRef}>
          <ConfirmSelectionButton onPress={() => confirm()} isDisabled={selectedImages.length === 0 || isConfirming} />
        </Portal>
      )}

      <Form onSubmit={handleGenerate} isDisabled={isGenerating} />

      <div className={Styles.previewContainer}>
        <Preview requestId={requestId} />
      </div>
    </div>
  )
}

export { AiGeneration }
